import { TestDefinition } from '../../../types/procedures';

// TypeScript interfaces
export interface OrderCode {
  code: string;
  description: string;
}

export interface FormState {
  orderType: string;
  orderCode: OrderCode | TestDefinition | null;
  selectedTestCodes: TestDefinition[];
  orderDescription: string;
  priority: string;
  status: string;
  dateOrdered: string;
  attachments: File[];
  orderingProvider: string;
  orderingFacility: string;
  selectedTest: string;
  selectedLabIntegrationId: string;
  notes: string;
  specimenType: string;
  fastingRequired: boolean;
  collectionTime: string;
  followUpNotes: string;
  showFollowUp: boolean;
  bodySite: string;
  laterality: string;
  showImagingReport: boolean;
  imagingReport: string;
  dicomFile: File | null;
  selectedCheckups: string[];
  checkupResults: Record<string, string>;
  createdCheckupOrders: any[];
  linkedDiagnosis: any[];
  requisitionOrderData?: any; // Order data for requisition form
  requisitionId: string; // Fixed requisition ID for this order form
  // Referral-specific fields
  selectedExternalProvider: any | null; // Selected NPI provider for referrals
}

// Utility to robustly extract a YYYY-MM-DD string from various orderDate formats
export function getDateStringFromOrderDate(orderDate: any): string {
  if (!orderDate) return new Date().toISOString().slice(0, 10);
  
  if (typeof orderDate === 'string') {
    // Try to parse as date string
    const d = new Date(orderDate);
    if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    // If it's already in YYYY-MM-DD, just return
    if (/^\d{4}-\d{2}-\d{2}$/.test(orderDate)) return orderDate;
    return new Date().toISOString().slice(0, 10);
  }
  
  if (typeof orderDate === 'object') {
    // Firestore Timestamp
    if ('seconds' in orderDate) {
      const d = new Date(orderDate.seconds * 1000);
      if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    }
    // JS Date
    if (orderDate instanceof Date && !isNaN(orderDate.getTime())) {
      return orderDate.toISOString().slice(0, 10);
    }
  }
  
  return new Date().toISOString().slice(0, 10);
}

// Helper to safely convert Firestore Timestamp or string to date string
export function toDateString(dateVal: any): string {
  if (!dateVal) return '';
  if (dateVal.toDate) return dateVal.toDate().toLocaleString();
  return new Date(dateVal).toLocaleString();
}

// Constants for order types and options
// Note: These are kept here for backward compatibility
// but the main form should use ORDER_TYPES from enums.ts
export const ORDER_TYPES = [
  'Lab',
  'Imaging', 
  'Procedure',
  'Surgical',
  'Physical Exam',
  'Referral',
  'Generic',
  'Intervention',
  'Risk Assessment',
];

export const PRIORITY_OPTIONS = ['STAT', 'Routine', 'ASAP'];
export const STATUS_OPTIONS = ['Draft', 'Ordered', 'Completed', 'Cancelled'];

export const ORDER_CODES = [
  { code: 'CPT123', description: 'CBC (Complete Blood Count)' },
  { code: 'LOINC456', description: 'Basic Metabolic Panel' },
  { code: 'SNOMED789', description: 'Chest X-Ray' },
  // Add more codes as needed
];

export const ICD10_CODES = [
  { code: 'E11.9', description: 'Type 2 diabetes mellitus without complications' },
  { code: 'I10', description: 'Essential (primary) hypertension' },
  // Add more codes as needed
];

export const CHECKUP_OPTIONS = [
  { code: 'BP', label: 'Blood Pressure' },
  { code: 'BMI', label: 'BMI' },
  { code: 'VISION', label: 'Vision Screening' },
  { code: 'HEARING', label: 'Hearing Screening' },
  { code: 'GLUCOSE', label: 'Glucose Screening' },
  // ...add more as needed
];

// Helper function to generate a unique requisition ID
function generateRequisitionId(): string {
  const year = new Date().getFullYear();
  const randomNum = Math.floor(Math.random() * 10000);
  return `LAB-REQ-${year}-${String(randomNum).padStart(4, '0')}`;
}

// Form reset utility
export function getInitialFormState(): FormState {
  return {
    orderType: '',
    orderCode: null,
    selectedTestCodes: [],
    orderDescription: '',
    priority: 'Routine',
    status: 'Saved as Draft',
    dateOrdered: new Date().toISOString().slice(0, 10),
    attachments: [],
    orderingProvider: 'Dr. John Doe',
    orderingFacility: '',
    selectedTest: '',
    selectedLabIntegrationId: '',
    notes: '',
    specimenType: '',
    fastingRequired: false,
    collectionTime: '',
    followUpNotes: '',
    showFollowUp: false,
    bodySite: '',
    laterality: '',
    showImagingReport: false,
    imagingReport: '',
    dicomFile: null,
    selectedCheckups: [],
    checkupResults: {},
    createdCheckupOrders: [],
    linkedDiagnosis: [],
    requisitionOrderData: undefined,
    requisitionId: generateRequisitionId(),
    // Referral-specific fields
    selectedExternalProvider: null,
  };
} 